import { EditablePreviewWorld, ObjectRuntimeState } from '../../types'
import { FRAME_SECONDS } from '../constants'
import { cloneBaseName, findStateByName } from '../sharedHelpers'
import { BehaviorHandler } from '../types'
import { concealServerStorageTemplate } from './serverStorageTemplate'

/**
 * ServerStorage 相当テンプレートの隠蔽と、学習者の control_clone 複製への
 * 状態復元（rjs07 / rjs08 / rjs10 / rjs12 で共有）。
 *
 * 実機の Clone() は ServerStorage に「保存された状態」（衝突・接触あり）を
 * 複製するが、プレビューの control_clone はワールド上の現在状態（隠し済み =
 * 衝突/接触オフ）を引き継ぐため、そのままだとクローンが当たらない・触れない。
 * 本 primitive はクローンの出現を毎フレーム監視し、出現直後に隠蔽前の
 * 保存状態（CanCollide / CanTouch）を書き戻すことで Clone() の意味論に
 * 合わせる。復元は ID ごとに一度だけ行い、以降に学習者プログラムが行う
 * setCanCollide 等の変更とは競合しない。
 *
 * conceal（同期）と watch（常駐）を分けているのは、ミッション側が
 * 「隠蔽前のスナップショットを複製元に使う」（rjs10 のブロック格子、
 * rjs12 の迷路壁）のと「クローン復元の常駐監視」を別の onPlay ハンドラで
 * 行えるようにするため。watch はスナップショットを closure 経由で参照する
 * ので、onPlay の登録順（どちらが先に走るか）に依存しない。
 *
 * 既知の制限（恒久対応は runtime 側の Clone() スナップショット化で別変更）:
 * 復元は毎フレームのポーリングのため、クローン生成から最大 1 フレーム
 * （約 33ms）は隠蔽状態のままになる。クローンの onPlay プログラムが起動
 * 直後に自分を control_clone する入れ子の連鎖では、復元前の隠蔽状態
 * （衝突/接触オフ）を引き継いだ孫クローン（元名#cloneN#cloneM）が生まれ、
 * 孫はテンプレート名と一致しないため復元対象にもならない競合が残る。
 */

export interface TemplateCloneRestorer {
  /**
   * テンプレートを隠し、隠蔽前の状態（複製元スナップショット）を
   * テンプレート名 → 状態で返す（同期）。見つかったテンプレートのみ対象。
   * onPlay ハンドラの同期部分（最初の await より前）で一度だけ呼ぶこと。
   */
  readonly concealAndSnapshot: (
    world: EditablePreviewWorld,
  ) => ReadonlyMap<string, ObjectRuntimeState>
  /**
   * クローン復元の常駐監視ハンドラ。onPlay に登録する。
   * concealAndSnapshot が記録したスナップショットを closure 経由で参照する
   * ため、隠蔽より先に走り出しても安全（隠蔽前は復元対象が無いだけ）。
   */
  readonly watchClones: BehaviorHandler
}

/**
 * テンプレート名の集合に対する隠蔽 + クローン復元のペアを作る。
 * 状態（スナップショット・復元済み ID）は closure に持ち、ミッションの
 * factory がプレイ毎に作り直すことでリセットする。
 */
export const createTemplateCloneRestorer = (
  templateNames: ReadonlyArray<string>,
): TemplateCloneRestorer => {
  // テンプレート名 → 隠蔽前の状態（Clone() が複製する「保存された状態」相当）。
  const snapshotByName = new Map<string, ObjectRuntimeState>()
  // テンプレート本体の ID（復元対象から除外する）。
  const templateIds = new Set<string>()
  // 復元済みクローン ID（ID ごとに一度だけ書き戻す）。
  const restoredIds = new Set<string>()

  const concealAndSnapshot = (
    world: EditablePreviewWorld,
  ): ReadonlyMap<string, ObjectRuntimeState> => {
    for (const name of templateNames) {
      const template = findStateByName(world, name)
      if (!template) {
        continue
      }
      snapshotByName.set(name, { ...template })
      templateIds.add(template.id)
      concealServerStorageTemplate(world, template.id)
    }
    return snapshotByName
  }

  /** 新規クローンへ保存状態の CanCollide / CanTouch を一度だけ書き戻す。 */
  const restoreNewClones = (world: EditablePreviewWorld): void => {
    for (const state of world.getState().values()) {
      if (templateIds.has(state.id) || restoredIds.has(state.id)) {
        continue
      }
      const baseName = cloneBaseName(state.name)
      const saved =
        baseName !== undefined ? snapshotByName.get(baseName) : undefined
      if (!saved) {
        continue
      }
      restoredIds.add(state.id)
      world.setCanCollide(state.id, saved.canCollide)
      world.setCanTouch(state.id, saved.canTouch)
    }
  }

  const watchClones: BehaviorHandler = async (ctx) => {
    while (!ctx.signal.aborted) {
      restoreNewClones(ctx.world)
      await ctx.wait(FRAME_SECONDS)
    }
  }

  return { concealAndSnapshot, watchClones }
}
